import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getFirestore, collection, getDocs, query, where } from "firebase/firestore";
import AdminShell from "../../admin/AdminShell";
import { useSession } from "../../../context/SessionContext";
import app, { useFirebaseEmulators } from "../../../firebase";
import { createManagedAccount } from "../../../services/studentAccountCreation";

const getValidationResolution = (field, message) => {
  if (message.includes('required')) {
    return field === 'password'
      ? 'Use Generate to create a password before saving.'
      : `Enter a ${field === 'email' ? 'valid email address' : field.toLowerCase()}.`;
  }
  if (message.includes('Maximum 15 characters')) {
    return `Shorten ${field.toLowerCase()} to 15 characters or fewer.`;
  }
  if (message.includes('only letters')) {
    return `Use letters, spaces, apostrophes, or hyphens in ${field.toLowerCase()}.`;
  }
  if (field === 'email' && message.includes('between 3 and 50')) {
    return 'Use an email address between 3 and 50 characters long.';
  }
  if (field === 'email') {
    return 'Enter an email address that includes "@" and ".com".';
  }
  return 'Review this field and correct its value.';
};

const getAccountCreationError = error => {
  const code = String(error?.code || '').toLowerCase();
  if (['auth/email-already-in-use', 'email-already-exists'].includes(code)) {
    return {
      title: 'Email already in use',
      problem: 'An account is already registered with this email address.',
      resolution: 'Enter a different email address that is not linked to another account.'
    };
  }
  if (['auth/invalid-email', 'invalid-email'].includes(code)) {
    return {
      title: 'Invalid email address',
      problem: error.message || 'The email address was rejected.',
      resolution: 'Check for typos and enter an email between 3 and 50 characters that includes "@" and ".com".'
    };
  }
  if (['auth/weak-password', 'auth/invalid-password'].includes(code)) {
    return {
      title: 'Password does not meet requirements',
      problem: error.message || 'The password was rejected.',
      resolution: 'Generate a new password. It must be between 6 and 128 characters.'
    };
  }
  if (['unauthenticated', 'caller_inactive', 'permission_denied'].includes(code)) {
    return {
      title: 'Account creation not authorized',
      problem: error.message || 'Your account cannot create this user.',
      resolution: 'Sign in with an active account that has permission to create users.'
    };
  }
  if (['deletion_in_progress', 'auth/deletion-in-progress'].includes(code)) {
    return {
      title: 'Account change in progress',
      problem: error.message || 'An account using this email is currently being removed.',
      resolution: 'Wait a few minutes, then try again. If the problem continues, contact a System Administrator.'
    };
  }
  return {
    title: 'Could not create account',
    problem: error?.message || 'The account creation request failed.',
    resolution: 'Check the entered details and your connection, then try again. If the problem continues, contact a System Administrator.'
  };
};

export default function AddUser({ onClose, onSaved }) {
  const navigate = useNavigate();
  const { currentUser, userRole, roleLoading, loading: authLoading } = useSession();
  const isPage = !onClose;
  const allowedCreatorRoles = ['System Administrator', 'Registrar', 'Admission Counselor'];
  const canCreateUsers = currentUser && !authLoading && !roleLoading && allowedCreatorRoles.includes(userRole);
  const [form, setForm] = useState({ firstName: '', middleName: '', lastName: '', school: '', department: '', email: '', password: '', role: '', status: 'Active' });
  const [showPassword, setShowPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [creationError, setCreationError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [schools, setSchools] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loadingSchools, setLoadingSchools] = useState(true);
  const [loadingDepartments, setLoadingDepartments] = useState(false);
  const [schoolError, setSchoolError] = useState('');
  const [departmentError, setDepartmentError] = useState('');

  const handleChange = (k, v) => {
    setForm(prev => ({
      ...prev,
      [k]: v,
      ...(k === 'school' ? { department: '' } : {}),
      ...(k === 'role' && ['Registrar', 'Admission Counselor', 'Room Manager', 'System Administrator'].includes(v) ? { school: '', department: '' } : {})
    }));
    if (k === 'email') {
      setFieldErrors(prev => ({ ...prev, email: '' }));
      setCreationError(null);
    }
  };

  const showSchoolDepartment = !['Registrar', 'Admission Counselor', 'Room Manager', 'System Administrator'].includes(form.role);

  useEffect(() => {
    const fetchSchools = async () => {
      setLoadingSchools(true);
      setSchoolError('');
      try {
        const db = getFirestore(app);
        const snapshot = await getDocs(collection(db, 'schools'));
        const schoolNames = snapshot.docs
          .map(doc => doc.data()?.name)
          .filter(Boolean)
          .sort((a, b) => a.localeCompare(b));
        setSchools([...new Set(schoolNames)]);
      } catch (err) {
        console.error('Failed to load schools', err);
        setSchoolError('Failed to load schools');
      } finally {
        setLoadingSchools(false);
      }
    };

    fetchSchools();
  }, []);

  useEffect(() => {
    if (!form.school) {
      setDepartments([]);
      return;
    }

    const fetchDepartments = async () => {
      setLoadingDepartments(true);
      setDepartmentError('');
      try {
        const db = getFirestore(app);
        const q = query(collection(db, 'departments'), where('school', '==', form.school));
        const snapshot = await getDocs(q);
        const deptItems = snapshot.docs
          .map(doc => doc.data())
          .filter(data => data?.name)
          .map(data => data.name)
          .sort((a, b) => a.localeCompare(b));
        setDepartments([...new Set(deptItems)]);
      } catch (err) {
        console.error('Failed to load departments', err);
        setDepartmentError('Failed to load departments');
      } finally {
        setLoadingDepartments(false);
      }
    };

    fetchDepartments();
  }, [form.school]);

  const generatePassword = () => {
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const digits = '0123456789';
    const symbols = '!@';
    const all = upper + lower + digits + symbols;
    const length = 10;

    const getRandomChar = (source, randValues, index) => source[randValues[index] % source.length];
    const makeRandomValues = (count) => {
      const values = new Uint32Array(count);
      if (window?.crypto?.getRandomValues) {
        window.crypto.getRandomValues(values);
      } else {
        for (let i = 0; i < count; i += 1) {
          values[i] = Math.floor(Math.random() * 0xffffffff);
        }
      }
      return values;
    };

    const rand = makeRandomValues(length);
    let password = '';
    password += getRandomChar(upper, rand, 0);
    password += getRandomChar(lower, rand, 1);
    password += getRandomChar(digits, rand, 2);
    password += getRandomChar(symbols, rand, 3);

    for (let i = 4; i < length; i += 1) {
      password += getRandomChar(all, rand, i);
    }

    password = password.split('').sort(() => 0.5 - Math.random()).join('');
    password = password.replace(/\s+/g, '');
    setForm(prev => ({ ...prev, password }));
    return password;
  };

  const validateFields = () => {
    const errors = {};
    const nameRegex = /^[A-Za-z\s'-]+$/;

    // First name
    if (!form.firstName || form.firstName.trim().length === 0) {
      errors.firstName = 'First name is required';
    } else if (!nameRegex.test(form.firstName)) {
      errors.firstName = 'First name must contain only letters';
    } else if (form.firstName.trim().length > 15) {
      errors.firstName = 'Maximum 15 characters';
    }

    // Last name
    if (!form.lastName || form.lastName.trim().length === 0) {
      errors.lastName = 'Last name is required';
    } else if (!nameRegex.test(form.lastName)) {
      errors.lastName = 'Last name must contain only letters';
    } else if (form.lastName.trim().length > 15) {
      errors.lastName = 'Maximum 15 characters';
    }

    // Middle name (optional but validated if present)
    if (form.middleName && form.middleName.trim().length > 0) {
      if (!nameRegex.test(form.middleName)) {
        errors.middleName = 'Middle name must contain only letters';
      } else if (form.middleName.trim().length > 15) {
        errors.middleName = 'Maximum 15 characters';
      }
    }

    // Email validations: length 3-50, must contain @ and .com
    const emailVal = (form.email || '').trim();
    if (!emailVal) {
      errors.email = 'Email is required';
    } else if (emailVal.length < 3 || emailVal.length > 50) {
      errors.email = 'Email must be between 3 and 50 characters';
    } else if (!emailVal.includes('@') || !emailVal.toLowerCase().includes('.com')) {
      errors.email = 'Email must contain "@" and ".com"';
    }

    // Password required
    if (!form.password || form.password.length === 0) {
      errors.password = 'Password is required';
    }

    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setCreationError({
        title: 'Please correct the account details',
        problem: 'Some fields do not meet the required format or character limits.',
        resolution: 'Correct each item below, then save again.',
        issues: Object.entries(errors).map(([field, message]) => ({
          field: field === 'firstName' ? 'First name' : field === 'lastName' ? 'Last name' : field === 'middleName' ? 'Middle name' : field === 'email' ? 'Email' : 'Password',
          message,
          resolution: getValidationResolution(field, message)
        }))
      });
      return false;
    }
    setCreationError(null);
    return true;
  };

  const createAuthAccount = async (email, displayName, password) => {
    try {
      if (!currentUser) {
        throw new Error('No authenticated user found when calling createStudentAccount.');
      }
      return await createManagedAccount({
        caller: currentUser,
        email,
        displayName,
        password,
        staffProfile: {
          firstName: form.firstName.trim(),
          middleName: form.middleName.trim(),
          lastName: form.lastName.trim(),
          school: form.school || "",
          department: form.department || "",
          role: form.role
        }
      });
    } catch (e) {
      console.warn('createStudentAccount failed', e);
      throw e;
    }
  };

  const getLoginUrlForRole = (role) => {
    const academicsRoles = ['Dean', 'Program Head', 'Professor'];
    const servicesRoles = ['Registrar', 'Admission Counselor', 'Room Manager'];
    if (academicsRoles.includes(role)) return 'https://enrollease-19c30.web.app/academics';
    if (servicesRoles.includes(role)) return 'https://enrollease-19c30.web.app/services';
    if (role === 'System Administrator') return 'https://enrollease-19c30.web.app/admin-login';
    return 'https://enrollease-19c30.web.app/';
  };

  const sendUserCredentialsEmail = async ({ firstName, lastName, role, email, password }) => {
    if (useFirebaseEmulators) return;
    const loginUrl = getLoginUrlForRole(role);
    const response = await fetch('https://sendapprovalemail-ag2nilnpda-uc.a.run.app', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        to: email,
        name: `${firstName || ''} ${lastName || ''}`.trim(),
        idNumber: '',
        program: role,
        password,
        template: 'userCredentials',
        loginUrl,
      }),
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Failed to send credentials email: ${text}`);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setCreationError(null);
    try {
      if (authLoading) {
        throw new Error('Auth is still loading. Please wait a moment and try again.');
      }
      if (!currentUser) {
        throw new Error('Login required to create a user. Please sign in first.');
      }
      if (!allowedCreatorRoles.includes(userRole)) {
        throw new Error('Your account is not authorized to create user accounts. Sign in with a System Administrator account.');
      }

      const displayName = [form.firstName, form.middleName, form.lastName].filter(Boolean).join(' ').trim();
      const normalizedEmail = form.email?.trim();
      let passwordToUse = form.password;

      // validate fields before proceeding
      if (!validateFields()) {
        return;
      }

      if (!normalizedEmail) {
        throw new Error('Email is required to create an Authentication user.');
      }

      const db = getFirestore(app);
      const [usersSnapshot, studentsSnapshot] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'students'))
      ]);
      const normalizedEmailForComparison = normalizedEmail.toLowerCase();
      const duplicateEmailExists = [usersSnapshot, studentsSnapshot].some(snapshot =>
        snapshot.docs.some(userDoc => {
          const existingEmail = userDoc.data()?.email;
          return typeof existingEmail === 'string' &&
            existingEmail.trim().toLowerCase() === normalizedEmailForComparison;
        })
      );
      if (duplicateEmailExists) {
        const message = 'This email is already used by another account';
        setFieldErrors(prev => ({ ...prev, email: message }));
        setCreationError({
          title: 'Email already in use',
          problem: `${normalizedEmail} is already registered to an existing user or student account.`,
          resolution: 'Enter a different email address that is not linked to another account.',
          issues: [{
            field: 'Email',
            message,
            resolution: 'Choose a unique email address, then save again.'
          }]
        });
        return;
      }

      if (!passwordToUse) {
        passwordToUse = generatePassword();
      }

      const authResult = await createAuthAccount(normalizedEmail, displayName, passwordToUse);
      if (!authResult?.uid) {
        throw new Error('Authentication account creation did not return a UID.');
      }

      const uid = authResult.uid;
      try {
        await sendUserCredentialsEmail({
          firstName: form.firstName,
          lastName: form.lastName,
          role: form.role,
          email: normalizedEmail,
          password: passwordToUse,
        });
      } catch (emailErr) {
        console.error('Failed to send credentials email', emailErr);
        setCreationError({
          title: 'Account created, but email was not sent',
          problem: emailErr.message || 'The account was created, but the credentials email could not be sent.',
          resolution: 'Do not submit this form again because the account already exists. Check the email service, then use the account recovery or password reset process to provide access.'
        });
        return;
      }

      if (onSaved) onSaved();
      if (onClose) return onClose();
      navigate('/admin/users');
    } catch (err) {
      console.error('AddUser save failed', err);
      setCreationError(getAccountCreationError(err));
    } finally {
      setSaving(false);
    }
  };

  const content = (
    <div style={{ width: isPage ? '100%' : 760, maxWidth: isPage ? 900 : '96%', background: '#fff', borderRadius: 10, padding: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, color: '#155c2b' }}>Create User</h3>
        {onClose ? (
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', fontSize: 18, cursor: 'pointer' }}>✕</button>
        ) : (
          <button onClick={() => navigate('/admin/users')} style={{ background: '#fff', border: '1px solid #dfe7e3', color: '#155c2b', padding: '8px 12px', borderRadius: 8, cursor: 'pointer' }}>Back to Users</button>
        )}
      </div>

      <div style={{ marginTop: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>
            <label style={{ fontWeight: 700 }}>Role</label>
            <select value={form.role} onChange={e => handleChange('role', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: '1px solid #e6e9ec', boxSizing: 'border-box' }}>
              <option value="">Select role</option>
              <option value="Dean">Dean</option>
              <option value="Program Head">Program Head</option>
              <option value="Professor">Professor</option>
              <option value="Registrar">Registrar</option>
              <option value="Admission Counselor">Admission</option>
              <option value="Room Manager">Room Manager</option>
              <option value="System Administrator">System Administrator</option>
            </select>
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={{ fontWeight: 700 }}>First Name</label>
            <input value={form.firstName} onChange={e => handleChange('firstName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: fieldErrors.firstName ? '1px solid crimson' : '1px solid #e6e9ec', boxSizing: 'border-box' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={{ fontWeight: 700 }}>Last Name</label>
            <input value={form.lastName} onChange={e => handleChange('lastName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: fieldErrors.lastName ? '1px solid crimson' : '1px solid #e6e9ec', boxSizing: 'border-box' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={{ fontWeight: 700 }}>Middle Name</label>
            <input value={form.middleName} onChange={e => handleChange('middleName', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: fieldErrors.middleName ? '1px solid crimson' : '1px solid #e6e9ec', boxSizing: 'border-box' }} />
          </div>
          {showSchoolDepartment && (
            <>
              <div style={{ minWidth: 0 }}>
                <label style={{ fontWeight: 700 }}>School</label>
                <select value={form.school} onChange={e => handleChange('school', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: '1px solid #e6e9ec', boxSizing: 'border-box' }}>
                  <option value="">{loadingSchools ? 'Loading schools...' : 'Select school'}</option>
                  {schools.map(school => (
                    <option key={school} value={school}>{school}</option>
                  ))}
                </select>
                {schoolError && <div style={{ color: 'crimson', marginTop: 6 }}>{schoolError}</div>}
              </div>
              <div style={{ minWidth: 0 }}>
                <label style={{ fontWeight: 700 }}>Department</label>
                <select value={form.department} onChange={e => handleChange('department', e.target.value)} disabled={!form.school || loadingDepartments} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: '1px solid #e6e9ec', boxSizing: 'border-box' }}>
                  <option value="">{form.school ? (loadingDepartments ? 'Loading departments...' : 'Select department') : 'Choose a school first'}</option>
                  {departments.map(department => (
                    <option key={department} value={department}>{department}</option>
                  ))}
                </select>
                {departmentError && <div style={{ color: 'crimson', marginTop: 6 }}>{departmentError}</div>}
              </div>
            </>
          )}
          <div style={{ minWidth: 0 }}>
            <label style={{ fontWeight: 700 }}>Email</label>
            <input value={form.email} onChange={e => handleChange('email', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: fieldErrors.email ? '1px solid crimson' : '1px solid #e6e9ec', boxSizing: 'border-box' }} />
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={{ fontWeight: 700 }}>Password</label>
            <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  style={{ flex: '1 1 160px', minWidth: 0, boxSizing: 'border-box', border: fieldErrors.password ? '1px solid crimson' : '1px solid #e6e9ec', padding: 10, borderRadius: 8 }}
                  readOnly
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(visible => !visible)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #dfe7e3', background: '#fff', color: '#155c2b', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              <button type="button" onClick={generatePassword} style={{ padding: '10px 14px', borderRadius: 8, border: 'none', background: '#155c2b', color: '#fff', cursor: 'pointer', whiteSpace: 'nowrap' }}>Generate</button>
            </div>
          </div>
          <div style={{ minWidth: 0 }}>
            <label style={{ fontWeight: 700 }}>Status</label>
            <select value={form.status} onChange={e => handleChange('status', e.target.value)} style={{ width: '100%', padding: 10, marginTop: 6, borderRadius: 8, border: '1px solid #e6e9ec', boxSizing: 'border-box' }}>
              <option>Active</option>
              <option>Inactive</option>
            </select>
          </div>
        </div>

        {(!authLoading && !currentUser) && (
        <div style={{ color: '#b45309', background: '#fef3c7', padding: 12, borderRadius: 8, marginBottom: 12 }}>
          You must sign in as a System Administrator before creating users. Go to <strong>/admin-login</strong> and sign in with your admin email.
        </div>
      )}
      {currentUser && roleLoading && (
        <div style={{ color: '#1f2937', background: '#f3f4f6', padding: 12, borderRadius: 8, marginBottom: 12 }}>
          Loading permissions for <strong>{currentUser.email}</strong>...
        </div>
      )}
      {currentUser && !roleLoading && !canCreateUsers && (
        <div style={{ color: '#b45309', background: '#fef3c7', padding: 12, borderRadius: 8, marginBottom: 12 }}>
          Your account role is <strong>{userRole || 'unknown'}</strong>. Only System Administrators, Registrars, or Admission Counselors can create users.
        </div>
      )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
          {onClose && <button onClick={onClose} style={{ background: '#fff', border: '1px solid #ddd', padding: '10px 14px', borderRadius: 8 }}>Cancel</button>}
          <button onClick={handleSave} disabled={saving || authLoading || !canCreateUsers} style={{ background: saving || authLoading || !canCreateUsers ? '#9ca3af' : '#155c2b', color: '#fff', border: 'none', padding: '10px 16px', borderRadius: 8, fontWeight: 700, cursor: saving || authLoading || !canCreateUsers ? 'not-allowed' : 'pointer' }}>{saving ? 'Saving...' : 'Save'}</button>
        </div>
      </div>
      {creationError && (
        <div
          role="presentation"
          onMouseDown={event => {
            if (event.target === event.currentTarget) setCreationError(null);
          }}
          style={{ position: 'fixed', inset: 0, zIndex: 2100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, background: 'rgba(15, 23, 42, 0.55)' }}
        >
          <section
            role="alertdialog"
            aria-modal="true"
            aria-labelledby="add-user-error-title"
            aria-describedby="add-user-error-problem"
            style={{ width: '100%', maxWidth: 500, maxHeight: '85vh', overflowY: 'auto', padding: 24, borderRadius: 12, background: '#fff', boxShadow: '0 18px 48px rgba(0,0,0,0.24)' }}
          >
            <h2 id="add-user-error-title" style={{ margin: '0 0 12px', color: '#8a1c1c', fontSize: 21 }}>{creationError.title}</h2>
            <p id="add-user-error-problem" style={{ margin: '0 0 10px', color: '#334155', lineHeight: 1.55 }}>{creationError.problem}</p>
            {creationError.issues?.length > 0 && (
              <ul style={{ margin: '0 0 14px', paddingLeft: 22 }}>
                {creationError.issues.map(issue => (
                  <li key={issue.field} style={{ marginBottom: 10, color: '#334155', lineHeight: 1.5 }}>
                    <strong>{issue.field}: {issue.message}.</strong>
                    <div style={{ color: '#475569' }}>How to fix: {issue.resolution}</div>
                  </li>
                ))}
              </ul>
            )}
            <p style={{ margin: '0 0 20px', padding: 12, borderRadius: 8, background: '#f8fafc', color: '#475569', lineHeight: 1.5 }}>
              <strong>How to resolve: </strong>{creationError.resolution}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setCreationError(null)}
                style={{ padding: '9px 18px', border: 'none', borderRadius: 8, background: '#155c2b', color: '#fff', fontWeight: 700, cursor: 'pointer' }}
              >
                Close
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  );

  if (isPage) {
    return (
      <AdminShell title="Add User">
        <div style={{ maxWidth: 900, margin: '0 auto' }}>{content}</div>
      </AdminShell>
    );
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}>
      {content}
    </div>
  );
}
